import { useEffect, useRef, useState } from "react";
import { Camera, RotateCcw, Square, Upload, Video } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { ErrorBlock, InlineAlert } from "@/components/ui/States";

const MIN_DURATION_SEC = 30;
const MAX_DURATION_SEC = 60;

interface VideoRecorderProps {
  hints?: ReadonlyArray<string>;
  onSave: (blob: Blob, durationSec: number) => void | Promise<void>;
}

type RecorderState =
  | "idle"
  | "permission-denied"
  | "ready"
  | "countdown"
  | "recording"
  | "review"
  | "uploading"
  | "unsupported";

/**
 * Browser video recorder for meetup participants. Circle preview, 30–60 sec,
 * countdown, retake, auto-save. Falls back to file upload when MediaRecorder
 * isn't available.
 */
export function VideoRecorder({ hints, onSave }: VideoRecorderProps) {
  const previewVideoRef = useRef<HTMLVideoElement | null>(null);
  const playbackVideoRef = useRef<HTMLVideoElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef<number>(0);
  const tickerRef = useRef<number | null>(null);
  const stopTimerRef = useRef<number | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  const [state, setState] = useState<RecorderState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [secondsElapsed, setSecondsElapsed] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordedDuration, setRecordedDuration] = useState(0);
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    if (recordedBlob) {
      const url = URL.createObjectURL(recordedBlob);
      objectUrlRef.current = url;
    } else {
      objectUrlRef.current = null;
    }
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [recordedBlob]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const hasMediaDevices = Boolean(navigator.mediaDevices?.getUserMedia);
    const hasMediaRecorder = typeof window.MediaRecorder !== "undefined";
    if (!hasMediaDevices || !hasMediaRecorder) setState("unsupported");
  }, []);

  function stopAllTimers() {
    if (tickerRef.current !== null) {
      window.clearInterval(tickerRef.current);
      tickerRef.current = null;
    }
    if (stopTimerRef.current !== null) {
      window.clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
  }

  function stopStream() {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }

  useEffect(() => {
    return () => {
      stopAllTimers();
      stopStream();
    };
  }, []);

  function pickMimeType(): string {
    if (typeof window === "undefined" || typeof MediaRecorder === "undefined") {
      return "video/webm";
    }
    // MP4 first: WebKit's MediaRecorder (Safari iOS/iPadOS) does support
    // WebM/VP9 output but writes broken cluster timestamps (`tbr=1k tbn=1k`)
    // that confuse `<video>` seeking — preview ends up black and seeking is
    // off by 30x. MP4/H.264 from WebKit is well-formed. Chromium browsers
    // don't expose MP4 from MediaRecorder, so they cleanly fall through to
    // the WebM candidates where their VP8/VP9 output is fine.
    const candidates = [
      "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
      "video/mp4;codecs=h264,aac",
      "video/mp4",
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp8,opus",
      "video/webm",
    ];
    for (const c of candidates) {
      if (MediaRecorder.isTypeSupported(c)) return c;
    }
    return "";
  }

  async function requestPermission() {
    setError(null);
    try {
      // 540p is plenty for a talking-head pitch shown in a 200×200 circle —
      // the bitrate/resolution drop cuts file size roughly in half (5–6 MB
      // for 60 s vs. 10–12 MB at 720p) which is the single biggest upload
      // speedup we can do without changing the protocol.
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 960 }, height: { ideal: 540 }, facingMode: "user" },
        audio: true,
      });
      streamRef.current = stream;
      if (previewVideoRef.current) {
        previewVideoRef.current.srcObject = stream;
        await previewVideoRef.current.play().catch(() => {});
      }
      setState("ready");
    } catch (err) {
      setState("permission-denied");
      setError(
        err instanceof Error
          ? err.message
          : "Couldn't access your camera. Check browser permissions.",
      );
    }
  }

  function startCountdown() {
    setCountdown(3);
    setState("countdown");
    let n = 3;
    tickerRef.current = window.setInterval(() => {
      n -= 1;
      if (n <= 0) {
        window.clearInterval(tickerRef.current!);
        tickerRef.current = null;
        beginRecording();
      } else {
        setCountdown(n);
      }
    }, 1000);
  }

  function beginRecording() {
    if (!streamRef.current) return;
    const mimeType = pickMimeType();
    const recorder = new MediaRecorder(streamRef.current, {
      ...(mimeType ? { mimeType } : {}),
      // 800 kbps is the sweet spot for 540p talking-head on VP8/VP9; pushing
      // beyond that on this content adds bytes without visible quality gain.
      videoBitsPerSecond: 800_000,
      audioBitsPerSecond: 96_000,
    });
    mediaRecorderRef.current = recorder;
    chunksRef.current = [];

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, {
        type: chunksRef.current[0]?.type ?? "video/webm",
      });
      const duration = Math.round((Date.now() - startedAtRef.current) / 1000);
      setRecordedBlob(blob);
      setRecordedDuration(duration);
      stopAllTimers();
      stopStream();
      setState("review");
      if (duration >= MIN_DURATION_SEC && blob.size <= 24 * 1024 * 1024) {
        void autoSave(blob, duration);
      }
    };
    recorder.start(250);
    // eslint-disable-next-line react-hooks/purity -- runs from countdown timer.
    startedAtRef.current = Date.now();
    setSecondsElapsed(0);
    setState("recording");

    tickerRef.current = window.setInterval(() => {
      setSecondsElapsed((s) => {
        const next = s + 1;
        if (next >= MAX_DURATION_SEC) stopRecording();
        return next;
      });
    }, 1000);
  }

  function stopRecording() {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
  }

  function retake() {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setRecordedBlob(null);
    setRecordedDuration(0);
    setSecondsElapsed(0);
    void requestPermission();
  }

  async function autoSave(blob: Blob, duration: number) {
    setState("uploading");
    setError(null);
    try {
      await onSave(blob, duration);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed. Try again.");
      setState("review");
    }
  }

  function handleFileFallback(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.currentTarget.files?.[0];
    if (!file) return;
    setRecordedBlob(file);
    setRecordedDuration(MIN_DURATION_SEC);
    if (file.size <= 24 * 1024 * 1024) {
      void autoSave(file, MIN_DURATION_SEC);
    } else {
      setError("Video file must be under 24 MB. Try a shorter or lower-quality recording.");
      setState("review");
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {hints && hints.length > 0 ? (
        <InlineAlert tone="info">
          <p className="font-semibold">Hints</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-4">
            {hints.map((h) => (
              <li key={h}>{h}</li>
            ))}
          </ul>
        </InlineAlert>
      ) : null}

      {error && state !== "permission-denied" ? <ErrorBlock message={error} /> : null}

      {state === "unsupported" ? (
        <InlineAlert tone="info">
          Your browser doesn't support in-browser recording. You can upload a video file instead.
        </InlineAlert>
      ) : null}

      {state === "permission-denied" ? (
        <div className="rounded-[20px] border border-[#fed7aa] bg-[#fffbeb] p-5">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white text-xl">
              📷
            </div>
            <div>
              <p className="text-[15px] font-bold text-[#78350f]">Camera access blocked</p>
              <p className="mt-0.5 text-[12px] text-[#92400e]">
                Two minutes to fix — here's how:
              </p>
            </div>
          </div>
          <ol className="mt-4 flex flex-col gap-3">
            <PermissionStep
              n={1}
              title="Open site settings"
              text={
                <>
                  Tap the lock icon 🔒 next to <strong>this site</strong> in your browser's address
                  bar.
                </>
              }
            />
            <PermissionStep
              n={2}
              title="Allow camera & microphone"
              text={
                <>
                  Find <em>Camera</em> and <em>Microphone</em>, switch both to{" "}
                  <strong>Allow</strong>.
                </>
              }
            />
            <PermissionStep
              n={3}
              title="Reload"
              text={<>Pull down to refresh, or tap the button below.</>}
            />
          </ol>
          <Button onClick={() => window.location.reload()} fullWidth className="mt-5">
            ↻ I've allowed it — reload
          </Button>
          <p className="mt-3 text-center text-[11px] uppercase tracking-wide text-[#94a3b8]">
            Or
          </p>
          <label className="mt-3 inline-flex w-full">
            <input
              type="file"
              accept="video/mp4,video/webm,video/quicktime"
              className="hidden"
              onChange={handleFileFallback}
            />
            <span className="inline-flex h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-[12px] border border-[#e2e8f0] bg-white px-6 text-[14px] font-semibold text-[#1a1a1a] hover:bg-[#f8fafc]">
              <Upload className="h-4 w-4" /> Upload a video file instead
            </span>
          </label>
          <p className="mt-2 text-center text-[12px] text-[#6c757d]">
            Profiles with a recorded pitch get more matches than text-only.
          </p>
        </div>
      ) : null}

      <div
        className={`relative mx-auto h-[300px] w-[300px] overflow-hidden rounded-full border-[3px] bg-black transition-colors ${
          state === "recording" ? "border-[#ef4444] animate-pulse-ring" : "border-[#2563eb]"
        }`}
      >
        {state === "review" && objectUrlRef.current ? (
          <video
            ref={playbackVideoRef}
            src={objectUrlRef.current}
            controls
            playsInline
            className="h-full w-full object-cover"
          />
        ) : (
          <video
            ref={previewVideoRef}
            muted
            playsInline
            className="h-full w-full object-cover"
            style={{ transform: "scaleX(-1)" }}
          />
        )}

        {state === "countdown" ? (
          <div className="absolute inset-0 grid place-items-center bg-black/50">
            <span className="text-7xl font-bold text-white">{countdown}</span>
          </div>
        ) : null}

        {state === "recording" ? (
          <div className="absolute inset-x-0 top-4 flex flex-col items-center gap-2">
            <div className="flex items-center gap-2 rounded-full bg-[#ef4444] px-4 py-2 text-[15px] font-bold text-white shadow-lg">
              <span className="inline-block h-2.5 w-2.5 animate-pulse rounded-full bg-white" />
              Recording · {MAX_DURATION_SEC - secondsElapsed}s
            </div>
            {secondsElapsed < MIN_DURATION_SEC ? (
              <div className="rounded-full bg-black/70 px-3 py-1 text-[12px] font-semibold text-[#fbbf24] backdrop-blur-sm">
                {MIN_DURATION_SEC - secondsElapsed}s minimum
              </div>
            ) : null}
          </div>
        ) : null}

        {state === "idle" ? (
          <div className="absolute inset-0 grid place-items-center text-white">
            <div className="text-center">
              <Video className="mx-auto h-10 w-10 opacity-80" />
              <p className="mt-2 text-[13px]">Camera will appear here</p>
            </div>
          </div>
        ) : null}

        {state === "permission-denied" ? (
          <div className="absolute inset-0 grid place-items-center bg-black/60 text-white">
            <div className="text-center px-4">
              <p className="text-[13px] font-semibold">Camera access blocked</p>
              <p className="mt-1 text-[11px] opacity-80">See instructions below</p>
            </div>
          </div>
        ) : null}

        {state === "uploading" ? (
          <div className="absolute inset-0 grid place-items-center bg-black/40 text-white">
            <div className="flex flex-col items-center gap-2">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              <p className="text-[13px] font-semibold">Uploading...</p>
            </div>
          </div>
        ) : null}
      </div>

      <p className="text-center text-[12px] text-[#94a3b8]">
        {MIN_DURATION_SEC}–{MAX_DURATION_SEC} seconds · look into the camera
      </p>

      <div className="flex flex-wrap items-center justify-center gap-2">
        {state === "idle" || state === "unsupported" ? (
          <>
            {state !== "unsupported" ? (
              <Button onClick={requestPermission} leadingIcon={<Camera className="h-4 w-4" />}>
                Allow camera
              </Button>
            ) : null}
            <label className="inline-flex">
              <input
                type="file"
                accept="video/mp4,video/webm,video/quicktime"
                className="hidden"
                onChange={handleFileFallback}
              />
              <span className="inline-flex h-12 cursor-pointer items-center gap-2 rounded-[12px] border border-[#e2e8f0] bg-white px-6 text-[15px] font-semibold text-[#1a1a1a] hover:bg-[#f8fafc]">
                <Upload className="h-4 w-4" /> Upload a file
              </span>
            </label>
          </>
        ) : null}

        {state === "ready" ? (
          <Button onClick={startCountdown} leadingIcon={<Camera className="h-4 w-4" />}>
            Start recording
          </Button>
        ) : null}

        {state === "recording" ? (
          <Button
            variant="destructive"
            onClick={stopRecording}
            disabled={secondsElapsed < 5}
            leadingIcon={<Square className="h-4 w-4" fill="currentColor" />}
          >
            Stop Recording
          </Button>
        ) : null}

        {state === "review" ? (
          <>
            <Button
              variant="secondary"
              onClick={retake}
              leadingIcon={<RotateCcw className="h-4 w-4" />}
            >
              Re-record
            </Button>
            {recordedDuration < MIN_DURATION_SEC ? (
              <span className="text-[12px] font-semibold text-[#991b1b]">
                Too short — re-record a longer video.
              </span>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}

function PermissionStep({
  n,
  title,
  text,
}: {
  n: number;
  title: string;
  text: React.ReactNode;
}) {
  return (
    <li className="flex items-start gap-3">
      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-white text-[12px] font-bold text-[#92400e]">
        {n}
      </span>
      <div>
        <p className="text-[13px] font-bold text-[#78350f]">{title}</p>
        <p className="mt-0.5 text-[12px] text-[#92400e] leading-relaxed">{text}</p>
      </div>
    </li>
  );
}
